/** Layer & sync endpoints (contract: src/shared/dto/annotations.ts) and failure classification. */
import type {
  AnnotationRow,
  LayerDTO,
  PullRequest,
  PushAnnotationsResponse,
  ShareLayerRequest,
  UpsertLayerRequest,
} from '../../../shared/dto/annotations';
import { ApiFailure, NetworkFailure, api } from '../../api/client';
import type { PullPayload } from './merge';
import { classifyStatus } from './queue';

const layerPath = (id: string) => `/layers/${encodeURIComponent(id)}`;

export const transport = {
  push(ops: AnnotationRow[]): Promise<PushAnnotationsResponse> {
    return api<PushAnnotationsResponse>('/sync/annotations', { method: 'POST', body: { ops } });
  },
  pull(req: PullRequest): Promise<PullPayload> {
    return api<PullPayload>('/sync/pull', { method: 'POST', body: req, timeoutMs: 30_000 });
  },
  upsertLayer(req: UpsertLayerRequest): Promise<unknown> {
    return api<unknown>('/layers', { method: 'POST', body: req });
  },
  renameLayer(id: string, name: string): Promise<unknown> {
    return api<unknown>(layerPath(id), { method: 'PATCH', body: { name } });
  },
  deleteLayer(id: string): Promise<unknown> {
    return api<unknown>(layerPath(id), { method: 'DELETE' });
  },
  shareLayer(id: string, req: ShareLayerRequest): Promise<LayerDTO> {
    return api<LayerDTO>(`${layerPath(id)}/share`, { method: 'PUT', body: req });
  },
};

/**
 * Outcome of one network step: 'offline' (unreachable) and 'server' (5xx/429/unexpected) are
 * retried later with backoff; 'auth' (401) stops until the user signs in again; 'fail' is a
 * permanent rejection of that request.
 */
export type NetOutcome = 'ok' | 'offline' | 'server' | 'auth' | 'fail';

export function classifyError(e: unknown): Exclude<NetOutcome, 'ok'> {
  if (e instanceof NetworkFailure) return 'offline';
  if (e instanceof ApiFailure) {
    const c = classifyStatus(e.status);
    return c === 'auth' ? 'auth' : c === 'retry' ? 'server' : 'fail';
  }
  const name = (e as { name?: string } | null)?.name;
  if (name === 'AbortError' || name === 'TimeoutError') return 'offline';
  return 'server';
}

export function failureOf(e: unknown, at: number): { status: number; code: string; at: number } {
  if (e instanceof ApiFailure) return { status: e.status, code: e.code, at };
  return { status: 0, code: 'internal', at };
}

/** A LayerDTO-shaped response (the layer endpoints may also answer 204). */
export function asLayerDTO(v: unknown): LayerDTO | null {
  if (!v || typeof v !== 'object') return null;
  const o = (v as { layer?: unknown }).layer && typeof (v as { layer?: unknown }).layer === 'object' ? (v as { layer: object }).layer : v;
  const l = o as Partial<LayerDTO>;
  return typeof l.id === 'string' && typeof l.ownerId === 'string' && typeof l.sheetFileId === 'string' ? (l as LayerDTO) : null;
}
